import React, { useState, useEffect, useRef } from 'react';
import { FileText, Printer, Download, Plus, Trash2, UserPlus } from 'lucide-react';
import { referenceAPI } from '../../../../api/reference';
import { toast } from 'react-toastify';
import { useAuth } from '../../../../state/auth/useAuth';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CARD_STYLES, BUTTON_STYLES, INPUT_STYLES } from '../../../receptionist-dashboard/utils/constants';
import { useToast } from '../../../../hooks/useToast';

const Reference = ({ patient }) => {
  const { user } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  const [currentReference, setCurrentReference] = useState({
    speciality: '',
    toDoctorName: '',
    notes: ''
  });

  // Fetch references when patient changes
  useEffect(() => {
    if (patient?._id || patient?.id || patient?.patientId) {
      fetchReferences();
    } else {
      setReferences([]);
    }
  }, [patient]);

  // Fetch references from API
  const fetchReferences = async () => {
    const patientId = patient?._id || patient?.id || patient?.patientId;
    if (!patientId) return;

    setLoading(true);
    try {
      const { data } = await referenceAPI.getReferences({ patientId, limit: 100 });
      
      setReferences(data?.references || []);
      console.log("references", references);
    } catch (error) {
      console.error('Error fetching references:', error);
      showError(error.message || 'Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCurrentReference(prev => ({ ...prev, [field]: value }));
  };

  const addReference = async () => {
    const patientId = patient?._id || patient?.id || patient?.patientId;
    if (!patientId) {
      showError('Please select a patient from the queue');
      return;
    }

    if (!currentReference.toDoctorName.trim() && !currentReference.speciality.trim()) {
      showError('Please provide either a specialist doctor name or speciality');
      return;
    }

    const doctorId = user?._id || user?.id;
    if (!doctorId) {
      showError('Unable to identify current doctor');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        patientId,
        assignedDoctorId: doctorId,
        speciality: currentReference.speciality,
        toDoctorName: currentReference.toDoctorName,
        notes: currentReference.notes
      };

      await referenceAPI.createReference(payload);
      showSuccess('Reference added successfully');
      
      // Reset form
      setCurrentReference({
        speciality: '',
        toDoctorName: '',
        notes: ''
      });

      // Refresh list
      await fetchReferences();
    } catch (error) {
      console.error('Error adding reference:', error);
      showError(error.message || 'Failed to add reference');
    } finally {
      setLoading(false);
    }
  };

  const deleteReference = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reference?')) {
      return;
    }

    setLoading(true);
    try {
      await referenceAPI.deleteReference(id);
      showSuccess('Reference deleted successfully');
      await fetchReferences();
    } catch (error) {
      console.error('Error deleting reference:', error);
      showError(error.message || 'Failed to delete reference');
    } finally {
      setLoading(false);
    }
  };

  const togglePrintHistory = async (id, currentValue) => {
    setLoading(true);
    try {
      await referenceAPI.updateReference(id, { printHistory: !currentValue });
      showSuccess('Print history updated');
      await fetchReferences();
    } catch (error) {
      console.error('Error updating print history:', error);
      showError(error.message || 'Failed to update print history');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (references.length === 0) {
      showError('No references to print');
      return;
    }
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (references.length === 0) {
      showError('No references to download');
      return;
    }

    setLoading(true);
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`doctor-references-${patient?.patientId || 'patient'}.pdf`);
      showSuccess('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndPrint = async () => {
    if (currentReference.toDoctorName.trim() || currentReference.speciality.trim()) {
      await addReference();
    }
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6" style={{fontFamily: ' sans-serif'}}>
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-area, #printable-area * {
              visibility: visible;
            }
            #printable-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      <div className={`${CARD_STYLES.highlight} p-5 no-print border-2 border-gray-200`}>
        <div className="flex items-center gap-3 mb-3">
          <FileText className="w-6 h-6 text-[#2D869C]" />
          <h1 className="text-xl font-bold text-gray-700" style={{fontFamily: 'sans-serif'}}>Doctor References</h1>
        </div>
        <p className="text-lg text-gray-500 font-medium">
          {patient
            ? `Manage referrals for ${patient.firstName || patient.fullName || patient.name || 'Patient'} ${patient.lastName || ''}`
            : 'Select a patient to manage doctor references'}
        </p>
      </div>

      {/* Add New Reference */}
      <div className={`${CARD_STYLES.default} p-6 no-print border-2 border-gray-200`}>
        <div className="flex items-center gap-2 mb-5">
          <UserPlus className="w-6 h-6 text-[#2D869C]" />
          <h1 className="text-xl font-bold text-gray-700" style={{fontFamily: 'sans-serif'}}>Add New Reference</h1>
        </div>
        
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-lg font-semibold text-gray-500 mb-3" style={{fontFamily: ' sans-serif'}}>
                Speciality <span className="text-[#E99797]">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Neurology, Cardiology"
                value={currentReference.speciality}
                onChange={(e) => handleInputChange('speciality', e.target.value)}
                disabled={loading}
                className={`px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900 font-medium w-full ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
            </div>
            <div>
              <label className="block text-lg font-semibold text-[#838383] mb-3" style={{fontFamily: ' sans-serif'}}>
                Referred Doctor Name <span className="text-[#E99797]">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Dr. Johnson"
                value={currentReference.toDoctorName}
                onChange={(e) => handleInputChange('toDoctorName', e.target.value)}
                disabled={loading}
                className={`px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900 font-medium w-full ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          <div>
            <label className="block text-lg font-semibold text-[#838383] mb-3" style={{fontFamily: ' sans-serif'}}>Notes</label>
            <textarea
              rows={4}
              placeholder="Referral notes and evaluation details..."
              value={currentReference.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              disabled={loading}
              className={`px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-transparent text-lg text-gray-900 font-medium w-full resize-none ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          </div>

          <div className="flex items-center justify-end gap-3 mt-2">
            <button
              onClick={addReference}
              disabled={loading || !patient}
              style={{fontFamily: ' sans-serif'}}
              className="px-5 py-2.5 bg-[#2D869C] text-white rounded-lg hover:bg-[#9DC9D4] transition-colors font-semibold text-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              {loading ? 'Adding...' : 'Add Reference'}
            </button>
          </div>
        </div>
      </div>

      {/* Printable References List */}
      <div ref={printRef} id="printable-area">
        {loading && references.length === 0 ? (
          <div className={`${CARD_STYLES.default} p-8 text-center no-print`}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D869C] mx-auto"></div>
            <p className="mt-4 text-[#838383] font-medium">Loading references...</p>
          </div>
        ) : references.length > 0 ? (
          <div className={`${CARD_STYLES.default} p-6 border-2 border-gray-200`}>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-[#2D869C] mb-3">Doctor Referral</h2>
              <div className="pb-4 mb-4 border-b-2 border-gray-200">
                <div className="grid grid-cols-2 gap-6 text-lg">
                  <div>
                    <p className="text-gray-600 font-medium mb-1">Patient Name:</p>
                    <p className="font-bold text-gray-900" style={{fontFamily:'sans-serif'}}>{references[0]?.patientId?.firstName || patient?.firstName || patient?.fullName || patient?.name || 'N/A'} {references[0]?.patientId?.lastName || patient?.lastName || ''}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium mb-1">Patient ID:</p>
                    <p className="font-bold text-gray-900">{references[0]?.patientId?.externalId || patient?.externalId || patient?.patientId || patient?.id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium mb-1">Referring Doctor:</p>
                    <p className="font-bold text-gray-900">{references[0]?.assignedDoctorId?.firstName && references[0]?.assignedDoctorId?.lastName ? `Dr. ${references[0].assignedDoctorId.firstName} ${references[0].assignedDoctorId.lastName}` : user?.firstName && user?.lastName ? `Dr. ${user.firstName} ${user.lastName}` : user?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium mb-1">Date:</p>
                    <p className="font-bold text-gray-900">{formatDate(new Date())}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-5 no-print">
              <h4 className="text-xl font-bold text-[#838383]">
                Referrals ({references.length})
              </h4>
            </div>
            
            <div className="space-y-5">
              {references.map((reference, index) => (
                <div key={reference._id} className="bg-gray-50 rounded-lg border-2 border-gray-200 p-5 hover:bg-[#9DC9D4]/10 transition-colors duration-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-4">
                        <span className="text-2xl">👨‍⚕️</span>
                        <h5 className="text-xl font-bold text-[#2D869C]">
                          {reference.toDoctorName || 'N/A'}
                        </h5>
                        {reference.speciality && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-md font-semibold bg-[#9DC9D4]/30 text-[#2D869C]">
                            {reference.speciality}
                          </span>
                        )}
                      </div>
                      
                      {reference.notes && (
                        <div className="mb-4">
                          <p className="text-lg font-semibold text-[#838383] mb-2">Notes:</p>
                          <p className="text-[#838383] text-lg font-medium bg-white p-4 rounded-lg border border-gray-200">
                            {reference.notes}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => deleteReference(reference._id)}
                      disabled={loading}
                      className="ml-4 text-[#E99797] hover:text-[#E99797]/80 p-3 disabled:opacity-50 no-print rounded-lg hover:bg-[#E99797]/10 transition-colors duration-200 border-2 border-[#E99797]"
                      title="Delete reference"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl border-2 border-gray-200 p-12 text-center no-print">
            <UserPlus className="mx-auto h-16 w-16 text-[#2D869C]/40" />
            <h3 className="mt-4 text-xl font-bold text-[#838383]">No references yet</h3>
            <p className="mt-2 text-lg text-[#838383]/70 font-medium">
              {patient ? 'Add a doctor reference above to get started.' : 'Select a patient to add references.'}
            </p>
          </div>
        )}
      </div>

      {/* Bottom Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-6 no-print">
        <button
          onClick={handleDownloadPDF}
          disabled
          className="px-5 py-2.5 bg-[#2D869C] text-white rounded-lg transition-colors font-semibold text-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="w-5 h-5" />
          Save as PDF
        </button>
        
        <button
          onClick={handlePrint}
          disabled
          className="px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Printer className="w-5 h-5" />
          Print
        </button>
      </div>
    </div>
  );
};

export default Reference;
